import { isApiError } from '#shared/core/errors';

/**
 * Guests added with no signal. A guest is a roster row on the server, so adding one mid-setup on
 * a court with one bar needs the same care as a game: keep it on this phone, show it in the picker
 * at once, and send it the moment the phone is back online. The queue is a small list in IndexedDB
 * (`kvSet`), replayed on `online`, `visibilitychange` and a 15 s beat.
 *
 * Games that start a game with such a guest await `flushPendingGuests()` (players/public.ts) before
 * they queue the game. Offline that sends nothing, and the game create can beat the guest when signal
 * returns; guestHeal.ts puts a create refused for that reason back in the outbox.
 */

export interface PendingGuest {
  id: string;
  displayName: string;
  nickname: string | null;
  createdAt: number;
}

export interface GuestStore {
  load(): Promise<PendingGuest[]>;
  save(list: PendingGuest[]): Promise<void>;
}

export interface GuestQueue {
  /** Remember a guest that could not be sent yet. */
  add(g: PendingGuest): Promise<void>;
  list(): Promise<PendingGuest[]>;
  /**
   * Try to send everyone. A network failure keeps the guest for next time; the server saying no
   * (a 4xx) drops it, because retrying a rejected guest forever would help nobody. Returns the ids
   * that reached the server.
   */
  flush(): Promise<string[]>;
}

/** The phone is offline or the request never got an answer: anything that is not the server's own error. */
export const isOffline = (e: unknown): boolean => !isApiError(e);

export function createGuestQueue(store: GuestStore, send: (g: PendingGuest) => Promise<unknown>): GuestQueue {
  let running: Promise<string[]> | null = null;
  return {
    async add(g) {
      const list = await store.load();
      if (!list.some((x) => x.id === g.id)) await store.save([...list, g]);
    },
    list: () => store.load(),
    flush() {
      running ??= (async () => {
        const tried = await store.load();
        if (tried.length === 0) return []; // the 15 s beat calls this: no write when there is nothing to send
        const sent: string[] = [];
        const keep: PendingGuest[] = [];
        for (const g of tried) {
          try {
            await send(g);
            sent.push(g.id);
          } catch (e) {
            if (isOffline(e) || (isApiError(e) && e.status >= 500)) keep.push(g);
          }
        }
        // A guest added while we were sending is not in `tried`, and must survive the rewrite.
        const triedIds = new Set(tried.map((g) => g.id));
        const addedMeanwhile = (await store.load()).filter((g) => !triedIds.has(g.id));
        await store.save([...keep, ...addedMeanwhile]);
        return sent;
      })().finally(() => {
        running = null;
      });
      return running;
    },
  };
}
